/** Sub-PR E2 §5.3 · 功能專案頁「內容」（嵌在 ChatPage 版面主區·§5.5 保留左側列表/頂部列）。
 *
 * 上：功能圖示 + 名稱；中：開場說明 + 輸入框 + 開場選項（該範圍前 3-5 工具 display_name·可點）；
 * 下：本專案歷史對話。送第一句或點選項 → onStart（由 ChatPage 建對話並帶第一句）。
 * §5.9 D10：白名單功能加「編輯」按鈕 → onEdit（直接開 modal·不呼叫 AI）。
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Button, Chip, List, ListItemButton, ListItemText, Stack, TextField, Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';

import { useMessage } from '../../../../contexts/MessageContext';
import { getI18nValue } from '../../../../utils/i18nHelper';
import type { SystemFunction } from '../../../../types';
import { ChatSession, getAvailableTools, listSessions } from '../services/aiChatService';
import { isEmbeddable } from './FunctionModal';

interface OpeningOption { name: string; label: string; }

interface Props {
  funcCode: string;
  funcObj: SystemFunction | null;
  onStart: (text: string) => void;          // 送第一句 / 點開場選項
  onEdit: () => void;                        // 開場「編輯」→ modal
  onSelectSession: (id: number) => void;
  busy?: boolean;
}

const ScopeProjectContent: React.FC<Props> = ({ funcCode, funcObj, onStart, onEdit, onSelectSession, busy }) => {
  const { t, i18n } = useTranslation();
  const { showApiError } = useMessage();
  const [options, setOptions] = React.useState<OpeningOption[]>([]);
  const [history, setHistory] = React.useState<ChatSession[]>([]);
  const [input, setInput] = React.useState('');

  React.useEffect(() => {
    (async () => {
      try {
        const r = await getAvailableTools(funcCode);
        setOptions(r.categories.flatMap((c) => c.tools)
          .filter((tl) => tl.name !== 'get_current_time' && tl.name !== 'open_function_page')
          .slice(0, 5)
          .map((tl) => ({ name: tl.name, label: (tl.display_name?.[i18n.language]) || tl.label || tl.name })));
      } catch { /* silent ok: 開場選項載入失敗不阻擋輸入 */ }
      try { setHistory(await listSessions(false, funcCode)); } catch (err) { showApiError(err); }
    })();
  }, [funcCode, i18n.language, showApiError]);

  const funcName = funcObj ? getI18nValue(funcObj.func_name, i18n.language) : funcCode;

  return (
    <Box sx={{ flex: 1, overflowY: 'auto', px: { xs: 2, md: 3 }, py: 4, maxWidth: 860, mx: 'auto', width: '100%' }}>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
        {funcObj?.func_icon ? `${funcObj.func_icon} ` : ''}{funcName}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('chat.scope.intro', '目前在「{{name}}」功能中，請說明您需要的服務。', { name: funcName })}
      </Typography>

      <TextField
        fullWidth multiline minRows={2} placeholder={t('chat.scope.inputPlaceholder', '輸入您的需求…')}
        value={input} onChange={(e) => setInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onStart(input); } }}
        disabled={busy}
      />
      <Stack direction="row" spacing={1} sx={{ mt: 1, mb: 3, flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
        <Button variant="contained" onClick={() => onStart(input)} disabled={busy || !input.trim()}>
          {t('chat.scope.send', '送出')}
        </Button>
        {isEmbeddable(funcCode) && (
          <Button variant="outlined" startIcon={<EditIcon />} onClick={onEdit}>
            {t('chat.scope.edit', '編輯{{name}}', { name: funcName })}
          </Button>
        )}
      </Stack>

      {options.length > 0 && (
        <>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            {t('chat.scope.suggestions', '您可以這樣說')}
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}>
            {options.map((o) => (
              <Chip key={o.name} label={o.label} onClick={() => onStart(o.label)} clickable disabled={busy} />
            ))}
          </Stack>
        </>
      )}

      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
        {t('chat.scope.history', '本功能的歷史對話')}
      </Typography>
      {history.length === 0 ? (
        <Typography variant="body2" color="text.disabled">{t('chat.scope.noHistory', '尚無對話')}</Typography>
      ) : (
        <List dense>
          {history.map((s) => (
            <ListItemButton key={s.id} onClick={() => onSelectSession(s.id)}>
              <ListItemText primary={s.title || t('chat.untitled', '（未命名對話）')}
                secondary={s.last_message_at || s.created_at} />
            </ListItemButton>
          ))}
        </List>
      )}
    </Box>
  );
};

export default ScopeProjectContent;
